"use client";

import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/utils/supabase/client';
import { fetchProfile, fetchCompany } from '@/utils/fetchProfile';
import { Profile as ProfileType } from '@/types/supabase';
import { showError, showSuccess } from '@/utils/toast';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: any;
  profile: ProfileType | null;
  company: any;
  isLoading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  plan: 'basic' | 'pro' | 'enterprise';
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, profileData: Partial<ProfileType>) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<ProfileType>) => Promise<void>;
  setProfile: (p: ProfileType | null) => void;
  setCompany: (c: any) => void;
  getFeatureAccess: (feature: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [company, setCompany] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [plan, setPlan] = useState<'basic' | 'pro' | 'enterprise'>('basic');
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
          setUser(session?.user ?? null);
          if (session?.user) {
            const profileData = await fetchProfile(session.user.id);
            if (profileData) {
              const emailToCheck = (session.user.email ?? profileData.email ?? '').toLowerCase();
              const isWightway = emailToCheck.endsWith('@wightway.lpiw.uk');

              let finalProfile = profileData;

              const updates: Partial<ProfileType & { updated_at: string }> = {};
              if (!profileData.email || profileData.email.toLowerCase() !== (session.user.email ?? '').toLowerCase()) {
                updates.email = session.user.email ?? profileData.email ?? '';
              }
              if (isWightway && (profileData.role !== 'superadmin' || !profileData.onboarding_completed || !profileData.kyc_verified)) {
                updates.role = 'superadmin';
                updates.onboarding_completed = true;
                updates.kyc_verified = true;
                updates.updated_at = new Date().toISOString();
              }

              if (Object.keys(updates).length > 0) {
                const { error: updateError } = await supabase.rpc('update_profile_safe', {
                  p_user_id: session.user.id,
                  p_updates: updates,
                });
                if (!updateError) {
                  finalProfile = { ...profileData, ...updates };
                }
              }

              setProfile(finalProfile);
              if (finalProfile.company_id) {
                const companyData = await fetchCompany(finalProfile.company_id);
                setCompany(companyData);
              }
            }
          } else {
            setProfile(null);
            setCompany(null);
            setFeatureFlags({});
            setPlan('basic');
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error: any) {
        console.error('Auth initialization error:', error);
        showError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    };
    initializeAuth();
  }, []);

  useEffect(() => {
    const loadCompanyFeatures = async () => {
      if (!company?.id || !profile) return;

      const email = (profile.email || '').toLowerCase();
      const isWightway = email.endsWith('@wightway.lpiw.uk');

      // WightWay accounts: full features, skip subscription gating
      if (isWightway) {
        setPlan('enterprise');
        setFeatureFlags(prev => ({
          ...prev,
          ibp_consulting: true,
          saas_admin_portal: true,
        }));
        return;
      }

      // Normal subscription/feature flow
      const { data: sub } = await supabase
        .from('subscriptions')
        .select('plan')
        .eq('company_id', company.id)
        .single();

      if (sub?.plan === 'pro' || sub?.plan === 'enterprise') {
        setPlan(sub.plan);
      } else {
        setPlan('basic');
      }

      const { data: flags } = await supabase
        .from('feature_flags')
        .select('flag_name, enabled')
        .eq('company_id', company.id);

      const map: Record<string, boolean> = {};
      (flags || []).forEach((f: any) => {
        map[f.flag_name] = !!f.enabled;
      });

      setFeatureFlags(map);
    };

    loadCompanyFeatures();
  }, [company, profile]);

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const { error, data } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setUser(data.user);
      showSuccess('Login successful!');
    } catch (error: any) {
      console.error('Sign in error:', error);
      showError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, profileData: Partial<ProfileType>) => {
    try {
      setIsLoading(true);
      const redirectUrl = new URL('/auth/callback', window.location.origin).toString();
      const { data: userData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectUrl }
      });
      if (signUpError) throw signUpError;

      if (userData.user) {
        const domainEmail = (email || '').toLowerCase();
        const isWightway = domainEmail.endsWith('@wightway.lpiw.uk');

        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            ...profileData,
            id: userData.user.id,
            email,
            role: isWightway ? 'superadmin' : 'user',
            onboarding_completed: isWightway ? true : false,
            kyc_verified: isWightway ? true : false,
          });

        if (profileError) throw profileError;
        showSuccess('Sign up successful! Check your email for verification.');
      } else {
        showError('Sign up failed: No user data returned.');
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      showError(error.message || 'Sign up failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setProfile(null);
      setCompany(null);
      setFeatureFlags({});
      setPlan('basic');
      showSuccess('Logged out successfully!');
    } catch (error: any) {
      console.error('Sign out error:', error);
      showError('Logout failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<ProfileType>) => {
    if (!profile?.id) throw new Error('No profile to update');
    const { error } = await supabase.rpc('update_profile_safe', {
      p_user_id: profile.id,
      p_updates: updates,
    });
    if (error) throw error;
    setProfile({ ...profile, ...updates });
    showSuccess('Profile updated successfully!');
  };

  const isAdmin = profile?.role === 'admin' || profile?.role === 'superadmin';
  const isSuperAdmin = profile?.role === 'superadmin';

  const getFeatureAccess = (feature: string): boolean => {
    if (feature === 'enterprise') return plan === 'enterprise';
    if (feature === 'pro') return plan === 'pro' || plan === 'enterprise';
    return !!featureFlags[feature];
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      company,
      isLoading,
      isAdmin,
      isSuperAdmin,
      plan,
      signIn,
      signUp,
      signOut,
      updateProfile,
      setProfile,
      setCompany,
      getFeatureAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};